import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@roger/ui-components';
import {
  Archive,
  ArchiveRestore,
  Loader2,
  Command,
  ChevronDown,
  FileSpreadsheet,
  FileCheck,
  FileX,
} from 'lucide-react';
import { bulkUpdateCodingTags } from '../services/rogerDocApi';
import type { BulkTagActionType } from '../services/rogerDocApi';
import type { CodingTags } from '../types/codingData.types';

import { useStore } from '../store';

type ActionType =
  | 'archive'
  | 'unarchive'
  | 'markF2fAttached'
  | 'markF2fNotAttached'
  | 'markOrderAttached'
  | 'markOrderNotAttached';

interface ActionConfig {
  label: string;
  confirmMessage: (count: number) => string;
  tags: CodingTags;
  actionDescriptor?: { type: BulkTagActionType };
}

const ACTION_CONFIGS: Record<ActionType, ActionConfig> = {
  archive: {
    label: 'Archive',
    confirmMessage: count =>
      count === 1
        ? 'Are you sure you want to archive this visit?'
        : `Are you sure you want to archive ${count} selected visits?`,
    tags: { archived: true },
  },
  unarchive: {
    label: 'Unarchive',
    confirmMessage: count =>
      count === 1
        ? 'Are you sure you want to unarchive this visit?'
        : `Are you sure you want to unarchive ${count} selected visits?`,
    tags: { archived: false },
  },
  markF2fAttached: {
    label: 'Mark F2F Attached & Signed',
    confirmMessage: count =>
      count === 1
        ? 'Mark this visit as F2F attached & signed?'
        : `Mark ${count} selected visits as F2F attached & signed?`,
    tags: { face_to_face_attached: true, face_to_face_signed: true },
    actionDescriptor: { type: 'mark_f2f_attached' },
  },
  markF2fNotAttached: {
    label: 'Mark F2F Not Attached & Not Signed',
    confirmMessage: count =>
      count === 1
        ? 'Mark this visit as F2F not attached & not signed?'
        : `Mark ${count} selected visits as F2F not attached & not signed?`,
    tags: { face_to_face_attached: false, face_to_face_signed: false },
    actionDescriptor: { type: 'mark_f2f_not_attached' },
  },
  markOrderAttached: {
    label: 'Mark Order Attached & Signed',
    confirmMessage: count =>
      count === 1
        ? 'Mark this visit as order attached & signed?'
        : `Mark ${count} selected visits as order attached & signed?`,
    tags: {
      home_health_order_attached: true,
      home_health_order_signed: true,
    },
    actionDescriptor: { type: 'mark_order_attached' },
  },
  markOrderNotAttached: {
    label: 'Mark Order Not Attached & Not Signed',
    confirmMessage: count =>
      count === 1
        ? 'Mark this visit as order not attached & not signed?'
        : `Mark ${count} selected visits as order not attached & not signed?`,
    tags: {
      home_health_order_attached: false,
      home_health_order_signed: false,
    },
    actionDescriptor: { type: 'mark_order_not_attached' },
  },
};

interface CodingActionsMenuProps {
  diagnosisIds: string[];
  onActionComplete?: () => void;
  onExportToExcel?: () => void;
  trigger?: React.ReactNode;
}

export const CodingActionsMenu: React.FC<CodingActionsMenuProps> = ({
  diagnosisIds,
  onActionComplete,
  onExportToExcel,
  trigger,
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [actionType, setActionType] = useState<ActionType | null>(null);
  const [loading, setLoading] = useState(false);

  const handleActionClick = (type: ActionType) => {
    setActionType(type);
    // Small delay to let dropdown close before opening dialog
    setTimeout(() => {
      setDialogOpen(true);
    }, 100);
  };

  const handleConfirm = async () => {
    if (!actionType || diagnosisIds.length === 0) return;

    const config = ACTION_CONFIGS[actionType];
    setLoading(true);
    try {
      const currentUser = useStore.getState().user;
      await bulkUpdateCodingTags({
        diagnosis_ids: diagnosisIds,
        tags: config.tags,
        ...(config.actionDescriptor && { action: config.actionDescriptor }),
        dashboard_user_email: currentUser?.email,
      });
      setDialogOpen(false);
      onActionComplete?.();
    } catch (error) {
      console.error(`Failed to ${actionType} visits:`, error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setDialogOpen(false);
    setActionType(null);
  };

  const count = diagnosisIds.length;
  const config = actionType ? ACTION_CONFIGS[actionType] : null;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          {trigger || (
            <button className='flex items-center gap-2 h-7 px-2 text-sm bg-black/20 border border-white/10 rounded hover:bg-black/30 transition-colors'>
              <Command className='w-3.5 h-3.5' />
              Actions
              <ChevronDown className='w-3.5 h-3.5' />
            </button>
          )}
        </DropdownMenuTrigger>
        <DropdownMenuContent align='end'>
          {onExportToExcel && (
            <DropdownMenuItem onClick={onExportToExcel}>
              <FileSpreadsheet className='w-4 h-4 mr-2' />
              Export to Excel
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onClick={() => handleActionClick('archive')}>
            <Archive className='w-4 h-4 mr-2 text-orange-600' />
            Archive
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => handleActionClick('unarchive')}>
            <ArchiveRestore className='w-4 h-4 mr-2 text-green-600' />
            Unarchive
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={() => handleActionClick('markF2fAttached')}
          >
            <FileCheck className='w-4 h-4 mr-2 text-green-600' />
            Mark F2F Attached & Signed
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => handleActionClick('markF2fNotAttached')}
          >
            <FileX className='w-4 h-4 mr-2 text-red-600' />
            Mark F2F Not Attached & Not Signed
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => handleActionClick('markOrderAttached')}
          >
            <FileCheck className='w-4 h-4 mr-2 text-blue-600' />
            Mark Order Attached & Signed
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => handleActionClick('markOrderNotAttached')}
          >
            <FileX className='w-4 h-4 mr-2 text-red-600' />
            Mark Order Not Attached & Not Signed
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog
        open={dialogOpen}
        onOpenChange={open => {
          if (!open) handleClose();
        }}
      >
        <DialogContent
          onCloseAutoFocus={e => e.preventDefault()}
          onOpenAutoFocus={e => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle>{config?.label}</DialogTitle>
          </DialogHeader>

          <div className='py-4'>
            <p className='text-gray-600'>{config?.confirmMessage(count)}</p>
          </div>

          <DialogFooter>
            <Button variant='outline' onClick={handleClose} disabled={loading}>
              No
            </Button>
            <Button onClick={handleConfirm} disabled={loading}>
              {loading && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
              Yes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
